import {Helmet} from '@dr.pogodin/react-helmet';

import {useParams} from 'react-router-dom';

import {defaultTo, isNil} from 'ramda';

import {
  useGetBillingInformationListQuery,
  useGetBranchListQuery,
  useGetCurrenciesQuery,
  useGetManufacturersQuery,
  useGetSeriesListQuery,
  useGetTenantQuery,
} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  useGetCodeListOptionsQuery,
  useGetDirectSaleVariantsQuery,
  useGetServiceOrderVariantsQuery,
  useGetSpacesQuery,
  useGetWarehousePricingEnumsQuery,
  useGetWarehouseQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {useGetSupplierCustomersQuery, usePermissions} from '@dms/shared';

import {WarehouseDetailForm} from './components/WarehouseDetailForm';

// eslint-disable-next-line complexity -- Pre-existing violation
export function WarehouseDetail() {
  const {id} = useParams();

  const [warehouseDictionaryEdit, warehouseDictionaryCreate] = usePermissions({
    permissionKeys: ['warehouseDictionaryEdit', 'warehouseDictionaryCreate'],
  });

  const {data: tenant, isLoading: isTenantLoading, isError: hasTenantError} = useGetTenantQuery();

  const {
    data: billingInformations,
    isLoading: areBillingInformationsLoading,
    isError: hasBillingInformationsError,
  } = useGetBillingInformationListQuery({includeDeactivated: true});

  const {
    data: warehouse,
    isLoading: isWarehouseLoading,
    isError: hasWarehouseError,
  } = useGetWarehouseQuery({warehouseId: defaultTo('', id)}, {skip: isNil(id)});

  // spaces can only be assigned when editing, the create (POST) endpoint does not support them
  const {
    data: spaces,
    isLoading: areSpacesLoading,
    isError: hasSpacesError,
  } = useGetSpacesQuery(undefined, {skip: isNil(id)});

  const {
    data: authorizationProfiles,
    isLoading: areAuthorizationProfilesLoading,
    isError: hasAuthorizationProfilesError,
  } = useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id ?? ''}, {skip: isNil(tenant)});

  const {
    data: branches,
    isLoading: areBranchesLoading,
    isError: hasBranchesError,
  } = useGetBranchListQuery();

  const {
    data: manufacturers,
    isLoading: areManufacturersLoading,
    isError: hasManufacturersError,
  } = useGetManufacturersQuery();

  const {
    data: warehousePricing,
    isLoading: isWarehousePricingLoading,
    isError: hasWarehousePricingError,
  } = useGetWarehousePricingEnumsQuery();

  const {
    data: currencies,
    isLoading: areCurrenciesLoading,
    isError: hasCurrenciesError,
  } = useGetCurrenciesQuery();

  const {
    data: salesPriceCalculationOptions,
    isLoading: areSalesPriceCalculationOptionsLoading,
    isError: hasSalesPriceCalculationOptionsError,
  } = useGetCodeListOptionsQuery({
    category: 'WRH-SALES-PRICE-CALCULATION',
  });

  const {
    data: baseSourcePriceOptions,
    isLoading: areBasePriceSourceOptionsLoading,
    isError: hasBaseSourcePriceOptionsError,
  } = useGetCodeListOptionsQuery({
    category: 'WRH-BASE-PRICE-SOURCE',
  });

  const {
    data: series,
    isLoading: areSeriesLoading,
    isError: hasSeriesError,
  } = useGetSeriesListQuery({
    type: [
      'warehouse/receipt',
      'warehouse/dispense',
      'warehouse/corrective-receipt',
      'warehouse/issue-note-correction',
    ],
  });

  const {
    data: serviceOrderVariants,
    isLoading: areServiceOrderVariantsLoading,
    isError: hasServiceOrderVariantsError,
  } = useGetServiceOrderVariantsQuery();

  const {
    data: directSaleVariants,
    isLoading: areDirectSaleVariantsLoading,
    isError: hasDirectSaleVariantsError,
  } = useGetDirectSaleVariantsQuery();

  const {
    data: suppliers,
    isLoading: areSuppliersLoading,
    isError: hasSuppliersError,
  } = useGetSupplierCustomersQuery();

  const isLoading =
    isTenantLoading ||
    areBillingInformationsLoading ||
    isWarehouseLoading ||
    areSpacesLoading ||
    areAuthorizationProfilesLoading ||
    areBranchesLoading ||
    areManufacturersLoading ||
    isWarehousePricingLoading ||
    areCurrenciesLoading ||
    areSalesPriceCalculationOptionsLoading ||
    areBasePriceSourceOptionsLoading ||
    areSeriesLoading ||
    areServiceOrderVariantsLoading ||
    areDirectSaleVariantsLoading ||
    areSuppliersLoading;

  const hasError =
    hasTenantError ||
    hasBillingInformationsError ||
    hasWarehouseError ||
    hasSpacesError ||
    hasAuthorizationProfilesError ||
    hasBranchesError ||
    hasManufacturersError ||
    hasWarehousePricingError ||
    hasCurrenciesError ||
    hasSalesPriceCalculationOptionsError ||
    hasBaseSourcePriceOptionsError ||
    hasSeriesError ||
    hasServiceOrderVariantsError ||
    hasDirectSaleVariantsError ||
    hasSuppliersError;

  // Check edit permission only if id is not nil that means that the user is creating a new measurement unit
  const isEditDisabled = isNil(id) ? !warehouseDictionaryCreate : !warehouseDictionaryEdit;

  const header: SettingsTemplateHeader = {
    title: isNil(id)
      ? i18n.t('entity.warehouse.labels.newWarehouse')
      : defaultTo('', warehouse?.name),
    breadcrumbs: [
      {
        label: i18n.t('entity.warehouse.labels.warehouses'),
        href: settingsRoutes.warehousesList,
      },
    ],
    alerts: isEditDisabled
      ? [
          {
            type: 'banner',
            variant: 'info',
            message: i18n.t('entity.warehouse.labels.missingWarehouseDictionaryEditPermission'),
          },
        ]
      : undefined,
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.warehouseDetail')} />
      <SettingsTemplate
        header={header}
        isLoading={isLoading}
        isError={hasError}
        data-testid={testIds.settings.warehousesDetail('warehouseDetailWrapper')}
      >
        <WarehouseDetailForm
          tenant={tenant}
          warehouse={warehouse}
          spaces={spaces}
          authorizationProfiles={authorizationProfiles}
          branches={branches}
          billingInformations={billingInformations?.billingInformationListItems}
          manufacturers={manufacturers}
          warehousePricing={warehousePricing}
          currencies={currencies}
          salesPriceCalculationOptions={salesPriceCalculationOptions}
          baseSourcePriceOptions={baseSourcePriceOptions}
          series={series}
          serviceOrderVariants={serviceOrderVariants}
          directSaleVariants={directSaleVariants}
          suppliers={suppliers}
          isEditDisabled={isEditDisabled}
          data-testid={testIds.settings.warehousesDetail('warehouseDetailForm')}
        />
      </SettingsTemplate>
    </>
  );
}
